import React, { useState, memo } from 'react';
import toast from 'react-hot-toast';
// This hook is provided by 'src/context/DataContext.js' and supplies all game data.
import { useDaggerheartData } from '../context/DataContext';
import EquipmentBrowser from './EquipmentBrowser';
import Modal from './common/Modal';

/**
 * LootBrowser Component (memoized to prevent re-renders on search input)
 */
const LootBrowser = memo(({
  lootData,
  lootSearchTerm,
  setLootSearchTerm,
  selectedLootCategory,
  setSelectedLootCategory,
  selectedLootRarity,
  setSelectedLootRarity,
  addLootToInventory
}) => {
  if (!lootData) {
    return <div>Loading loot data...</div>;
  }

  const { items, consumables } = lootData;
  const allLoot = [...items, ...consumables];

  const filteredLoot = allLoot.filter(item => {
    const matchesCategory = selectedLootCategory === 'all' ||
      (selectedLootCategory === 'items' && item.type !== 'consumable') ||
      (selectedLootCategory === 'consumables' && item.type === 'consumable') ||
      (selectedLootCategory === 'recipes' && item.type === 'recipe') ||
      (selectedLootCategory === 'relics' && item.type === 'relic') ||
      (selectedLootCategory === 'gems' && item.type === 'gem');

    const matchesRarity = selectedLootRarity === 'all' || item.rarity === selectedLootRarity;

    const matchesSearch = lootSearchTerm === '' ||
      item.name.toLowerCase().includes(lootSearchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(lootSearchTerm.toLowerCase());

    return matchesCategory && matchesRarity && matchesSearch;
  });

  const getRarityColor = (rarity) => {
    switch(rarity) {
      case 'common': return 'border-gray-400 bg-gray-50 dark:bg-gray-800';
      case 'uncommon': return 'border-green-400 bg-green-50 dark:bg-green-900/20';
      case 'rare': return 'border-blue-400 bg-blue-50 dark:bg-blue-900/20';
      case 'legendary': return 'border-purple-400 bg-purple-50 dark:bg-purple-900/20';
      default: return 'border-gray-400 bg-gray-50 dark:bg-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'consumable': return '🧪';
      case 'recipe': return '📜';
      case 'relic': return '💎';
      case 'gem': return '💍';
      case 'item':
      default: return '📦';
    }
  };

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <input
            type="text"
            placeholder="Search loot..."
            value={lootSearchTerm}
            onChange={(e) => setLootSearchTerm(e.target.value)}
            className="px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
          />

          <select
            value={selectedLootCategory}
            onChange={(e) => setSelectedLootCategory(e.target.value)}
            className="px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
          >
            <option value="all">All Categories</option>
            <option value="items">Items</option>
            <option value="consumables">Consumables</option>
            <option value="recipes">Recipes</option>
            <option value="relics">Relics</option>
            <option value="gems">Gems</option>
          </select>

          <select
            value={selectedLootRarity}
            onChange={(e) => setSelectedLootRarity(e.target.value)}
            className="px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
          >
            <option value="all">All Rarities</option>
            <option value="common">Common</option>
            <option value="uncommon">Uncommon</option>
            <option value="rare">Rare</option>
            <option value="legendary">Legendary</option>
          </select>

          <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
            Showing {filteredLoot.length} items
          </div>
        </div>
      </div>

      {/* Loot Grid */}
      <div className="grid gap-4 max-h-96 overflow-y-auto">
        {filteredLoot.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No loot matches your current filters.
          </div>
        ) : (
          filteredLoot.map(lootItem => (
            <div key={lootItem.id} className={`p-4 rounded-lg border-2 ${getRarityColor(lootItem.rarity)}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center">
                  <span className="text-xl mr-2">{getTypeIcon(lootItem.type)}</span>
                  <div>
                    <h4 className="font-bold text-lg">{lootItem.name}</h4>
                    <div className="flex items-center space-x-2 text-sm">
                      <span className={`px-2 py-1 rounded capitalize font-medium ${
                        lootItem.rarity === 'common' ? 'bg-gray-200 text-gray-800' :
                        lootItem.rarity === 'uncommon' ? 'bg-green-200 text-green-800' :
                        lootItem.rarity === 'rare' ? 'bg-blue-200 text-blue-800' :
                        'bg-purple-200 text-purple-800'
                      }`}>
                        {lootItem.rarity}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400 capitalize">
                        {lootItem.type}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => addLootToInventory(lootItem)}
                  className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                  Add to Inventory
                </button>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {lootItem.description}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
});

LootBrowser.displayName = 'LootBrowser';

/**
 * InventoryManager Component
 * Comprehensive inventory system for character equipment, items, and ingredients.
 * Handles both core and campaign frame equipment properly.
 *
 * @param {object} props - The component props.
 * @param {object} props.character - The current character data object.
 * @param {function} props.updateCharacter - Function to update the character state.
 * @param {function} props.onManagerClose - Function called when closing the manager.
 */
const InventoryManager = ({ character, updateCharacter, onManagerClose }) => {
  const allData = useDaggerheartData();
  const [activeTab, setActiveTab] = useState('equipment');
  const [isBrowsingEquipment, setIsBrowsingEquipment] = useState(false);
  const [isBrowsingLoot, setIsBrowsingLoot] = useState(false);
  
  // State for adding new items
  const [newItemName, setNewItemName] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState(1);
  const [newItemDescription, setNewItemDescription] = useState('');
  
  // Loot browser state
  const [lootSearchTerm, setLootSearchTerm] = useState('');
  const [selectedLootCategory, setSelectedLootCategory] = useState('all');
  const [selectedLootRarity, setSelectedLootRarity] = useState('all');
  
  // Early return if data is not loaded
  if (!allData) {
    return <div>Loading inventory data...</div>;
  }
  
  // Helper function to get weapon data from equipment database
  const getWeaponData = (weaponId) => {
    if (!weaponId) return null;
    
    // If weaponId is already an object, return it directly
    if (typeof weaponId === 'object' && weaponId.id) {
      return weaponId;
    }
    
    // Check equipment database (now flattened structure)
    if (allData.equipmentDatabase) {
      const { equipmentDatabase } = allData;
      
      // Search primary weapons (flattened structure: equipmentDatabase.primaryWeapons[itemKey])
      if (equipmentDatabase.primaryWeapons) {
        for (const itemKey in equipmentDatabase.primaryWeapons) {
          const weapon = equipmentDatabase.primaryWeapons[itemKey];
          if (weapon && weapon.id === weaponId) return weapon;
        }
      }
      
      // Search secondary weapons (flattened structure: equipmentDatabase.secondaryWeapons[itemKey])
      if (equipmentDatabase.secondaryWeapons) {
        for (const itemKey in equipmentDatabase.secondaryWeapons) {
          const weapon = equipmentDatabase.secondaryWeapons[itemKey];
          if (weapon && weapon.id === weaponId) return weapon;
        }
      }
    }
    
    // Fallback: check legacy weapon data
    if (allData.weapons && allData.weapons[weaponId]) {
      return allData.weapons[weaponId];
    }
    
    // Fallback: check campaign frame weapons
    if (character.campaignFrame !== 'core' && allData.campaignFrames && allData.campaignFrames[character.campaignFrame]) {
      const frameData = allData.campaignFrames[character.campaignFrame];
      if (frameData.startingEquipment) {
        // Check primary weapons
        const primaryWeapon = frameData.startingEquipment.primaryWeapons?.find(w => w.id === weaponId);
        if (primaryWeapon) return primaryWeapon;
        
        // Check secondary weapons
        const secondaryWeapon = frameData.startingEquipment.secondaryWeapons?.find(w => w.id === weaponId);
        if (secondaryWeapon) return secondaryWeapon;
      }
    }
    
    return null;
  };
  
  // Helper function to get armor data from equipment database
  const getArmorData = (armorId) => {
    if (!armorId) return null;
    
    // If armorId is already an object, return it directly
    if (typeof armorId === 'object' && armorId.id) {
      return armorId;
    }
    
    // Check equipment database (now flattened structure)
    if (allData.equipmentDatabase && allData.equipmentDatabase.armor) {
      for (const itemKey in allData.equipmentDatabase.armor) {
        const armor = allData.equipmentDatabase.armor[itemKey];
        if (armor && armor.id === armorId) return armor;
      }
    }
    
    // Fallback: check campaign frame armor (stored as array)
    if (character.campaignFrame !== 'core' && allData.campaignFrames && allData.campaignFrames[character.campaignFrame]) {
      const frameData = allData.campaignFrames[character.campaignFrame];
      if (frameData.startingEquipment && frameData.startingEquipment.armor) {
        // armor is an array, so find by id
        return frameData.startingEquipment.armor.find(armor => armor.id === armorId);
      }
    }
    
    return null;
  };
  
  // Use equipped structure if available, fallback to equipment
  const primaryWeapon = getWeaponData(character.equipped?.primaryWeapon || character.equipment?.primaryWeapon);
  const secondaryWeapon = getWeaponData(character.equipped?.secondaryWeapon || character.equipment?.secondaryWeapon);
  const armor = getArmorData(character.equipped?.armor || character.equipment?.armor);
  
  const addCustomItem = () => {
    if (!newItemName.trim()) return;
    
    const newItem = {
      id: `custom-${Date.now()}`,
      name: newItemName.trim(),
      description: newItemDescription.trim(),
      quantity: parseInt(newItemQuantity) || 1,
      type: 'custom'
    };
    
    const currentItems = character.inventory?.items || [];
    updateCharacter({
      ...character,
      inventory: {
        ...character.inventory,
        items: [...currentItems, newItem]
      }
    });
    
    // Reset form
    setNewItemName('');
    setNewItemDescription('');
    setNewItemQuantity(1);
  };

  const removeItem = (itemIndex) => {
    const currentItems = character.inventory?.items || [];
    updateCharacter({
      ...character,
      inventory: {
        ...character.inventory,
        items: currentItems.filter((_, index) => index !== itemIndex)
      }
    });
  };
  
  // Function to equip an item from inventory
  const equipItem = (item, index) => {
    console.log('equipItem called with:', { item, index });
    console.log('Current character equipment:', character.equipment);
    console.log('Current inventory equipment:', character.inventory?.equipment);
    
    // Remove item from inventory (or decrease quantity)
    const currentEquipment = character.inventory?.equipment || [];
    let updatedEquipment;
    
    if (item.quantity > 1) {
      // Decrease quantity
      updatedEquipment = [...currentEquipment];
      updatedEquipment[index] = { ...item, quantity: item.quantity - 1 };
    } else {
      // Remove item entirely
      updatedEquipment = currentEquipment.filter((_, i) => i !== index);
    }
    
    // Determine what type of equipment this is based on the item properties
    let equipmentSlot = {};
    let previouslyEquipped = null;
    
    // Check if it has weapon-like properties (damage, trait, range)
    if (item.damage && item.trait && item.range) {
      // It's a weapon - determine if primary or secondary based on actual equipment database categorization
      const weaponName = item.name.toLowerCase();
      const isShield = weaponName.includes('shield');
      
      // Check if this weapon is actually categorized as a secondary weapon in the equipment database
      let isSecondaryWeapon = isShield;
      
      // Look up in secondary weapons database to determine actual categorization
      if (allData.equipmentDatabase && allData.equipmentDatabase.secondaryWeapons) {
        for (const tierKey in allData.equipmentDatabase.secondaryWeapons) {
          const tier = allData.equipmentDatabase.secondaryWeapons[tierKey];
          for (const weaponKey in tier) {
            const weapon = tier[weaponKey];
            if (weapon && weapon.id === item.id) {
              isSecondaryWeapon = true;
              break;
            }
          }
          if (isSecondaryWeapon) break;
        }
      }
      
      // Fallback to name-based detection for non-database items (keeping the old logic as backup)
      if (!isSecondaryWeapon && !allData.equipmentDatabase) {
        isSecondaryWeapon = weaponName.includes('small dagger') || weaponName.includes('throwing') || 
                           weaponName.includes('whip') || weaponName.includes('grappler') || 
                           weaponName.includes('hand crossbow');
      }
      
      if (isSecondaryWeapon) {
        // Check if primary weapon is two-handed
        const currentPrimary = character.equipped?.primaryWeapon || character.equipment?.primaryWeapon;
        if (currentPrimary) {
          const primaryData = getWeaponData(currentPrimary);
          if (primaryData && primaryData.burden && primaryData.burden.toLowerCase().includes('two')) {
            toast.error('Cannot equip secondary weapon: Primary weapon is two-handed and requires both hands!');
            return;
          }
        }
        
        // It's a secondary weapon - check both structures for previously equipped
        previouslyEquipped = character.equipped?.secondaryWeapon || character.equipment?.secondaryWeapon;
        equipmentSlot.secondaryWeapon = item.id;
        console.log('Equipping as secondary weapon:', item.id);
      } else {
        // It's a primary weapon - check both structures for previously equipped
        previouslyEquipped = character.equipped?.primaryWeapon || character.equipment?.primaryWeapon;
        equipmentSlot.primaryWeapon = item.id;
        console.log('Equipping as primary weapon:', item.id);
        
        // Check for two-handed weapons (burden system)
        if (item.burden && item.burden.toLowerCase().includes('two')) {
          // Two-handed weapon unequips secondary weapon
          const currentSecondary = character.equipped?.secondaryWeapon || character.equipment?.secondaryWeapon;
          if (currentSecondary) {
            const secondaryData = getWeaponData(currentSecondary);
            if (secondaryData) {
              // Add secondary weapon back to inventory
              updatedEquipment.push({ ...secondaryData, quantity: 1 });
              console.log('Unequipped secondary weapon due to two-handed primary:', secondaryData.name);
            }
          }
          equipmentSlot.secondaryWeapon = ''; // Clear secondary slot
        }
      }
    } else if (item.baseArmorScore || item.name.toLowerCase().includes('armor') || 
               item.name.toLowerCase().includes('clothing') || item.name.toLowerCase().includes('mail')) {
      // It's armor - check both structures for previously equipped
      previouslyEquipped = character.equipped?.armor || character.equipment?.armor;
      equipmentSlot.armor = item.id;
      console.log('Equipping as armor:', item.id);
    } else {
      console.log('Could not determine equipment type for:', item.name);
      return; // Don't equip if we can't determine the type
    }
    
    // Add previously equipped item back to inventory (if there was one)
    if (previouslyEquipped && previouslyEquipped !== '') {
      // Find the previously equipped item data
      const previousItem = getWeaponData(previouslyEquipped) || getArmorData(previouslyEquipped);
      if (previousItem) {
        // Add it back to inventory
        updatedEquipment.push({ ...previousItem, quantity: 1 });
        console.log('Returning previously equipped item to inventory:', previousItem.name);
      }
    }
    
    const updatedCharacter = {
      ...character,
      // Update the equipped structure (primary)
      equipped: {
        ...character.equipped,
        ...equipmentSlot
      },
      // Also update the equipment structure for backward compatibility
      equipment: {
        ...character.equipment,
        ...equipmentSlot
      },
      inventory: {
        ...character.inventory,
        equipment: updatedEquipment
      }
    };
    
    console.log('Updated character being sent:', updatedCharacter);
    console.log('Equipment slot update:', equipmentSlot);
    
    // Update character with equipped item and updated inventory
    updateCharacter(updatedCharacter);
  };

  // Function to unequip an item and return it to inventory
  const unequipItem = (slot) => {
    const currentEquipped = character.equipped || {};
    const currentEquipment = character.equipment || {};
    const itemId = currentEquipped[slot] || currentEquipment[slot];
    
    if (!itemId) return; // Nothing to unequip
    console.log(`Unequipping ${slot}:`, itemId);
    
    // Get item data to add back to inventory
    const itemData = slot === 'armor' ? getArmorData(itemId) : getWeaponData(itemId);
    
    if (itemData) {
      // Add item back to inventory
      const currentInventoryEquipment = character.inventory?.equipment || [];
      const updatedInventoryEquipment = [...currentInventoryEquipment, { ...itemData, quantity: 1 }];
      
      // Remove from equipped slots
      const updatedEquipped = {
        ...currentEquipped,
        [slot]: '' // Clear the slot
      };
      
      // Also update the equipment structure for backward compatibility
      const updatedEquipmentStruct = {
        ...currentEquipment,
        [slot]: ''
      };
      
      // Update character
      const updatedCharacter = {
        ...character,
        equipped: updatedEquipped,
        equipment: updatedEquipmentStruct,
        inventory: {
          ...character.inventory,
          equipment: updatedInventoryEquipment
        }
      };
      
      console.log(`Unequipped ${slot}: ${itemData.name}, returned to inventory`);
      updateCharacter(updatedCharacter);
    }
  };

  // Function to add loot item to inventory
  const addLootToInventory = (lootItem) => {
    const currentItems = character.inventory?.items || [];
    const existingItem = currentItems.find(item => item.id === lootItem.id);
    
    let updatedItems;
    if (existingItem) {
      // Increase quantity for consumables, or just add for items
      if (lootItem.type === 'consumable') {
        const maxQuantity = 5; // Daggerheart rule: max 5 of each consumable
        const newQuantity = Math.min((existingItem.quantity || 1) + 1, maxQuantity);
        
        if (newQuantity > (existingItem.quantity || 1)) {
          updatedItems = currentItems.map(item => 
            item.id === lootItem.id 
              ? { ...item, quantity: newQuantity }
              : item
          );
        } else {
          toast.error(`You can only carry 5 of each consumable at a time!`);
          return;
        }
      } else {
        // For non-consumables, add as a new instance
        const newItem = {
          ...lootItem,
          id: `${lootItem.id}-${Date.now()}`,
          quantity: 1,
          source: 'loot'
        };
        updatedItems = [...currentItems, newItem];
      }
    } else {
      // Add new item
      const newItem = {
        ...lootItem,
        quantity: lootItem.type === 'consumable' ? 1 : 1,
        source: 'loot'
      };
      updatedItems = [...currentItems, newItem];
    }

    updateCharacter({
      ...character,
      inventory: {
        ...character.inventory,
        items: updatedItems
      }
    });

    toast.success(`Added ${lootItem.name} to inventory!`);
    setIsBrowsingLoot(false);
  };

  const EquipmentTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold">Equipped Items</h3>
      
      {/* Primary Weapon */}
      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300">Primary Weapon</h4>
          {primaryWeapon && (
            <button
              onClick={() => unequipItem('primaryWeapon')}
              className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
              title="Unequip primary weapon"
            >
              Unequip
            </button>
          )}
        </div>
        {primaryWeapon ? (
          <div>
            <p className="font-bold">{primaryWeapon.name}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {primaryWeapon.trait} | {primaryWeapon.range} | {primaryWeapon.damage} | {primaryWeapon.burden}
            </p>
            {primaryWeapon.feature && (
              <p className="text-xs italic text-gray-500">{primaryWeapon.feature}</p>
            )}
          </div>
        ) : (
          <div>
            <p className="font-bold">Unarmed</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Strength or Finesse | Melee | d4 physical | Uses Proficiency
            </p>
          </div>
        )}
      </div>
      
      {/* Secondary Weapon */}
      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300">Secondary Weapon</h4>
          {secondaryWeapon && (
            <button
              onClick={() => unequipItem('secondaryWeapon')}
              className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
              title="Unequip secondary weapon"
            >
              Unequip
            </button>
          )}
        </div>
        {secondaryWeapon ? (
          <div>
            <p className="font-bold">{secondaryWeapon.name}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {secondaryWeapon.trait} | {secondaryWeapon.range} | {secondaryWeapon.damage} | {secondaryWeapon.burden}
            </p>
            {secondaryWeapon.feature && (
              <p className="text-xs italic text-gray-500">{secondaryWeapon.feature}</p>
            )}
          </div>
        ) : (
          <div>
            {(() => {
              // Check if primary weapon is two-handed
              const primaryBurden = primaryWeapon?.burden;
              if (primaryBurden && primaryBurden.toLowerCase().includes('two')) {
                return <p className="text-gray-500 italic">Unavailable (Two-handed primary weapon)</p>;
              }
              return <p className="text-gray-500 italic">No secondary weapon equipped</p>;
            })()}
          </div>
        )}
      </div>
      
      {/* Armor */}
      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300">Armor</h4>
          {armor && (
            <button
              onClick={() => unequipItem('armor')}
              className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
              title="Unequip armor"
            >
              Unequip
            </button>
          )}
        </div>
        {armor ? (
          <div>
            <p className="font-bold">{armor.name}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Base Score: {armor.baseScore}
            </p>
            {armor.feature && (
              <p className="text-xs italic text-gray-500">{armor.feature}</p>
            )}
          </div>
        ) : (
          <p className="text-gray-500 italic">No armor equipped</p>
        )}
      </div>

      {/* Unequipped Inventory Items */}
      <div className="mt-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-bold">Inventory Equipment</h3>
          <button 
            onClick={() => setIsBrowsingEquipment(true)}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            Add Equipment
          </button>
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {(character.inventory?.equipment || []).length === 0 ? (
            <p className="text-gray-500 italic">No equipment in inventory</p>
          ) : (
            <div className="space-y-2">
              {character.inventory.equipment.map((item, index) => (
                <div key={`${item.id}-${index}`} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-bold">{item.name}</p>
                        {item.quantity > 1 && (
                          <span className="px-2 py-1 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs rounded-full">
                            x{item.quantity}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {item.trait && `${item.trait} | `}
                        {item.range && `${item.range} | `}
                        {item.damage && `${item.damage} | `}
                        {item.burden && item.burden}
                        {item.baseScore && `Base Score: ${item.baseScore}`}
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        Tier {item.tier} {item.type} {item.category && `(${item.category})`}
                      </p>
                      {item.feature && (
                        <p className="text-xs italic text-gray-500 mt-1">{item.feature}</p>
                      )}
                    </div>
                    <button
                      onClick={() => equipItem(item, index)}
                      className="ml-3 px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                    >
                      Equip
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
  
  const ItemsTab = () => (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-bold">Items</h3>
        <button 
          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          onClick={() => setIsBrowsingLoot(true)}
        >
          Add Items
        </button>
      </div>
      
      <div className="max-h-96 overflow-y-auto space-y-3">
        {(character.inventory?.items || []).length === 0 ? (
          <p className="text-gray-500 italic">No items in inventory</p>
        ) : (
          character.inventory.items.map((item, index) => {
            // Enhanced display for loot items
            const isLootItem = item.source === 'loot';
            const rarityColor = isLootItem ? (
              item.rarity === 'common' ? 'border-l-gray-400' :
              item.rarity === 'uncommon' ? 'border-l-green-400' :
              item.rarity === 'rare' ? 'border-l-blue-400' :
              item.rarity === 'legendary' ? 'border-l-purple-400' :
              'border-l-gray-400'
            ) : '';
            
            return (
              <div key={`${item.id}-${index}`} className={`p-3 bg-gray-50 dark:bg-gray-700 rounded-md flex justify-between items-start border-l-4 ${rarityColor}`}>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{item.name}</p>
                    {isLootItem && item.rarity && (
                      <span className={`px-2 py-1 text-xs rounded capitalize font-medium ${
                        item.rarity === 'common' ? 'bg-gray-200 text-gray-800' :
                        item.rarity === 'uncommon' ? 'bg-green-200 text-green-800' :
                        item.rarity === 'rare' ? 'bg-blue-200 text-blue-800' :
                        'bg-purple-200 text-purple-800'
                      }`}>
                        {item.rarity}
                      </span>
                    )}
                  </div>
                  {item.description && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{item.description}</p>}
                  <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                    {item.quantity && <span>Qty: {item.quantity}</span>}
                    {isLootItem && item.type && <span className="capitalize">{item.type}</span>}
                    {isLootItem && <span>Loot</span>}
                  </div>
                </div>
                <button
                  onClick={() => removeItem(index)}
                  className="ml-3 text-red-500 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
  
  const IngredientsTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold">Cooking Ingredients</h3>
      <p className="text-sm text-gray-500">
        Ingredients for Beast Feast cooking. These are used in the "Make a Feast" action.
      </p>
      
      <div className="space-y-2">
        {(character.inventory?.ingredients || []).length === 0 ? (
          <p className="text-gray-500 italic">No ingredients in inventory</p>
        ) : (
          character.inventory.ingredients.map((ingredient, index) => (
            <div key={`${ingredient.id}-${index}`} className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-md">
              <p className="font-medium">{ingredient.name}</p>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Flavors: {ingredient.flavors.map(f => `${f.profile} (${f.strength})`).join(', ')}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
      <h2 className="text-xl font-bold mb-3">Inventory</h2>
      
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 dark:border-gray-600 mb-4">
        <button
          onClick={() => setActiveTab('equipment')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'equipment'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Equipment
        </button>
        <button
          onClick={() => setActiveTab('items')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'items'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Items
        </button>
        {character.campaignFrame === 'beast-feast' && (
          <button
            onClick={() => setActiveTab('ingredients')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'ingredients'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Ingredients
          </button>
        )}
      </div>
      
      {/* Tab Content */}
      <div>
        {activeTab === 'equipment' && <EquipmentTab />}
        {activeTab === 'items' && <ItemsTab />}
        {activeTab === 'ingredients' && <IngredientsTab />}
      </div>

      {/* Equipment Browser Modal */}
      {isBrowsingEquipment && (
        <Modal title="Browse Equipment" onClose={() => setIsBrowsingEquipment(false)}>
          <EquipmentBrowser 
            character={character} 
            updateCharacter={updateCharacter} 
            onManagerClose={() => setIsBrowsingEquipment(false)}
          />
        </Modal>
      )}

      {/* Loot Browser Modal */}
      {isBrowsingLoot && (
        <Modal title="🏺 Browse Loot" onClose={() => setIsBrowsingLoot(false)}>
          <LootBrowser
            lootData={allData.loot}
            lootSearchTerm={lootSearchTerm}
            setLootSearchTerm={setLootSearchTerm}
            selectedLootCategory={selectedLootCategory}
            setSelectedLootCategory={setSelectedLootCategory}
            selectedLootRarity={selectedLootRarity}
            setSelectedLootRarity={setSelectedLootRarity}
            addLootToInventory={addLootToInventory}
          />
        </Modal>
      )}
    </div>
  );
};

export default InventoryManager;
